<template>
   <div v-bind:class="[{ busy: loadingImages}, { activeEnter: dragenter }]" class="image-gallery opacity-transition"
        ref="gallery">
      <pb-spinner v-if="loadingImages"></pb-spinner>

      <b-card no-body class="mb-3">
         <b-card-body>
            <b-row>
               <b-col>
                  <!-- Upload button and search -->
                  <b-row class="mb-3">
                     <b-col md="auto">
                        <b-button variant="primary" @click="triggerFileUpload">
                           <i class="fal fa-upload"></i> Upload Images
                        </b-button>
                        <input
                            type="file"
                            ref="fileInput"
                            @change="handleFileInputChange"
                            style="display: none"
                            multiple
                            accept="image/*">
                     </b-col>
                     <b-col>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalItems"
                            :per-page="pageSize"
                            aria-controls="imageList"
                        ></b-pagination>
                     </b-col>
                     <b-col>
                        <b-form @submit.prevent>
                           <b-input-group>
                              <b-form-input
                                  v-model="searchTerm"
                                  v-debounce:500="handleSearch"
                                  placeholder="Search images..."
                                  type="text">
                              </b-form-input>
                              <b-input-group-append>
                                 <b-input-group-text>
                                    <i class="fal fa-search"></i>
                                 </b-input-group-text>
                              </b-input-group-append>
                           </b-input-group>
                        </b-form>
                     </b-col>
                  </b-row>

                  <!-- Image grid -->
                  <b-row v-bind:class="[{ busy: uploadingImages}, 'opacity-transition']" v-if="imageList.length > 0">
                     <pb-spinner v-if="uploadingImages"></pb-spinner>
                     <b-col v-bind:key="image.id" md="3" class="my-3" v-for="image in imageList">
                        <div class="image-item-wrapper" @click="selectImage(image)">
                           <div class="image-card">
                              <img :src="fileResourceUrl(image.filename)" :alt="image.originalFilename" class="img-fluid">
                              <div class="image-overlay">
                                 <i class="fal fa-check-circle fa-2x"></i>
                              </div>
                           </div>
                           <div class="image-title">{{ image.originalFilename }}</div>
                        </div>
                     </b-col>
                  </b-row>

                  <!-- Empty state -->
                  <b-row v-else>
                     <b-col class="text-center py-5">
                        <i class="fal fa-images fa-3x text-muted mb-3"></i>
                        <p class="text-muted">No images found.</p>
                        <b-button variant="primary" @click="triggerFileUpload" class="mt-2">
                           <i class="fal fa-upload"></i> Upload Images
                        </b-button>
                        <p class="text-muted mt-3">Or drag and drop images here to upload.</p>
                     </b-col>
                  </b-row>

                  <!-- Bottom pagination -->
                  <b-row v-if="imageList.length > 0">
                     <b-col>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalItems"
                            :per-page="pageSize"
                            aria-controls="imageList"
                        ></b-pagination>
                     </b-col>
                  </b-row>
               </b-col>
            </b-row>
         </b-card-body>
      </b-card>
   </div>
</template>

<script>
import FileApi from "../../../shared/_api/file";

export default {
   name: 'ImageGallery',
   props: {
      selectImageCallback: Function
   },
   data() {
      return {
         currentPage: 1,
         pageSize: 12,
         totalItems: 0,
         imageList: [],
         loadingImages: false,
         searchTerm: '',
         dragenter: false,
         uploadingImages: false
      };
   },
   mounted() {
      this.setupDragAndDrop();
   },
   created() {
      this.loadImages();
   },
   watch: {
      currentPage() {
         this.loadImages();
      }
   },
   methods: {
      setupDragAndDrop() {
         ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(evt => {
            this.$refs.gallery.addEventListener(evt, (e) => {
               e.preventDefault();
               e.stopPropagation();
            }, false);
         });

         this.$refs.gallery.addEventListener('dragleave', () => {
            this.dragenter = false;
         });

         this.$refs.gallery.addEventListener('dragenter', () => {
            this.dragenter = true;
         });

         this.$refs.gallery.addEventListener('drop', (e) => {
            this.dragenter = false;
            const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
            if (files.length > 0) {
               this.uploadFiles(files);
            }
         });
      },

      loadImages() {
         this.loadingImages = true;

         const params = {
            page: this.currentPage - 1,
            size: this.pageSize,
            sortBy: 'createdAt',
            sortDir: 'desc',
            type: 'IMAGE'
         };

         if (this.searchTerm) {
            params.search = this.searchTerm;
         }

         FileApi.fetchMedia(params).then(response => {
            this.imageList = response.content || response;
            this.totalItems = response.totalElements || this.imageList.length;
         }).finally(() => {
            this.loadingImages = false;
         });
      },

      triggerFileUpload() {
         if (this.$refs.fileInput) {
            this.$refs.fileInput.click();
         }
      },

      handleFileInputChange(event) {
         const files = event.target.files;
         if (files && files.length > 0) {
            this.uploadFiles(Array.from(files));
         }
         // Reset the input so the same file can be selected again
         event.target.value = '';
      },

      uploadFiles(files) {
         if (files.length === 0) return;

         this.uploadingImages = true;
         let successCount = 0;
         let errorCount = 0;

         const uploadPromises = files.map(file =>
            FileApi.uploadMedia(file)
               .then(() => successCount++)
               .catch(() => errorCount++)
         );

         Promise.all(uploadPromises).then(() => {
            this.loadImages();

            if (successCount > 0) {
               this.$bvToast.toast(`Successfully uploaded ${successCount} image(s)`, {
                  title: 'Success',
                  variant: 'success',
                  autoHideDelay: 3000
               });
            }

            if (errorCount > 0) {
               this.$bvToast.toast(`Failed to upload ${errorCount} image(s)`, {
                  title: 'Error',
                  variant: 'danger',
                  autoHideDelay: 5000
               });
            }
         }).finally(() => {
            this.uploadingImages = false;
         });
      },

      handleSearch() {
         this.currentPage = 1;
         this.loadImages();
      },

      selectImage(image) {
         if (this.selectImageCallback) {
            this.selectImageCallback(image.filename);
         }
      }
   }
}
</script>

<style lang="scss" scoped>
.image-gallery {
   min-height: 300px;
}

.activeEnter {
   border: 2px dashed #007bff;
   background-color: rgba(0, 123, 255, 0.05);
}

.image-item-wrapper {
   cursor: pointer;
   transition: all 0.2s ease;

   &:hover {
      transform: translateY(-4px);

      .image-overlay {
         opacity: 1;
      }
   }

   .image-card {
      position: relative;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      overflow: hidden;
      background: #f8f9fa;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
         max-width: 100%;
         max-height: 100%;
         object-fit: contain;
      }

      .image-overlay {
         position: absolute;
         top: 0;
         left: 0;
         right: 0;
         bottom: 0;
         background: rgba(0, 123, 255, 0.8);
         display: flex;
         align-items: center;
         justify-content: center;
         color: white;
         opacity: 0;
         transition: opacity 0.2s ease;
      }
   }

   .image-title {
      margin-top: 8px;
      font-size: 0.875rem;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }
}
</style>
